import { BRAND } from "@/lib/api";
import { FileText, Copyright, ShieldCheck, Scale } from "lucide-react";

const EFFECTIVE = "Effective date: " + new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function TermsConditions() {
  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="gh-overline">Terms &amp; Conditions</span>
        <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-4">Terms &amp; Conditions</h1>
        <div className="gh-hairline my-7 max-w-[120px] mx-auto" />
        <p className="text-lg text-[#6B655F] leading-relaxed font-light">
          By accessing and using {BRAND.site}, you agree to the terms below. These terms protect both you and our small business so we can keep creating with care.
        </p>
        <p className="text-sm text-[#8A8175] mt-4">{EFFECTIVE}</p>
      </div>

      <div className="space-y-12 mt-16">
        <section id="acceptance" data-testid="terms-acceptance" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-[#8E9A86]" /></div>
            <h2 className="font-heading text-3xl text-[#3A3632]">Acceptance of Terms</h2>
          </div>
          <p className="text-[#6B655F] leading-relaxed">
            By browsing, purchasing from, or otherwise using this website, you accept these Terms &amp; Conditions in full. If you do not agree, please do not use the site. We may update these terms at any time, and the version posted here with the current effective date governs your use.
          </p>
        </section>

        <section id="ip" data-testid="terms-ip" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center shrink-0"><Copyright className="h-5 w-5 text-[#8E9A86]" /></div>
            <h2 className="font-heading text-3xl text-[#3A3632]">Intellectual Property Rights</h2>
          </div>
          <div className="space-y-4 text-[#6B655F] leading-relaxed">
            <p>
              All content displayed on {BRAND.site}, including but not limited to text, graphics, logos, 3D floral resin sculptural arrangements, custom gift box designs, assembly layout techniques, product photography, and digital code, is the exclusive property of Hope &amp; Favor and is protected by United States and international copyright, trademark, and intellectual property laws.
            </p>
            <p>
              You may not reproduce, modify, distribute, copy, republish, or use any materials from this website for commercial purposes without explicit, written permission from Hope &amp; Favor. Unauthorized use of our proprietary visual works or product imagery will result in immediate legal action and formal DMCA takedown filings.
            </p>
          </div>
        </section>

        <section id="conduct" data-testid="terms-conduct" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5 text-[#8E9A86]" /></div>
            <h2 className="font-heading text-3xl text-[#3A3632]">Use of the Website</h2>
          </div>
          <p className="text-[#6B655F] leading-relaxed">
            You agree to use this website lawfully and not to scrape, data-mine, screenshot for commercial use, or ingest our content for AI training. Orders, payments, shipping, returns, and privacy are governed by our <a href="/policies" className="text-[#8E9A86] underline">Policies</a>, which form part of these Terms.
          </p>
        </section>

        <section id="liability" data-testid="terms-liability" className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center shrink-0"><Scale className="h-5 w-5 text-[#8E9A86]" /></div>
            <h2 className="font-heading text-3xl text-[#3A3632]">Limitation of Liability</h2>
          </div>
          <p className="text-[#6B655F] leading-relaxed">
            To the fullest extent permitted by law, {BRAND.legal} is not liable for indirect, incidental, or consequential damages arising from the use of our products or website. Our total liability for any claim will not exceed the amount you paid for the order in question.
          </p>
        </section>

        <section className="bg-[#F2EFEB] border border-[#D9CBA0]/40 p-8">
          <h2 className="font-heading text-2xl text-[#3A3632] mb-3">Questions about these terms?</h2>
          <p className="text-[#6B655F] leading-relaxed">Reach {BRAND.owner} at <a href={`mailto:${BRAND.email}`} className="text-[#8E9A86] underline">{BRAND.email}</a> or <a href={`tel:${BRAND.phone}`} className="text-[#8E9A86] underline">{BRAND.phone}</a>.</p>
          <p className="text-xs text-[#8A8175] mt-4">These terms are provided as general terms for {BRAND.legal}. They are not legal advice; we recommend having them reviewed by an attorney for your state and situation.</p>
        </section>
      </div>
    </div>
  );
}
